import React, { createContext, useContext, useState, useCallback, ReactNode, useMemo, useEffect, useRef } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import { useAuth } from "./AuthContext";
import { useEncryption } from "./EncryptionContext";
import { InsightsChatMessage } from "@/utils/insights-chat-service";
import { v4 as uuidv4 } from "@/utils/uuid";
import { downloadEncryptedPayload, uploadEncryptedPayload } from "@/utils/encrypted-file-storage";

const INITIAL_MESSAGE = "Ask me anything about your entries. I can help you discover patterns, triggers, and trends.";
const DEV_BYPASS = import.meta.env.DEV && typeof window !== "undefined" && new URLSearchParams(window.location.search).has("bypass-auth");

interface StoredInsightsChatMessage extends InsightsChatMessage {
  imageStorageIds?: string[];
}

export interface InsightsChatThread {
  id: string;
  startedAt: Date;
  previewText?: string;
  messageCount: number;
}

interface InsightsChatPersistStore {
  threads: InsightsChatThread[];
  activeThreadId: string;
  /** Past chats list highlight; null until the user opens or starts a chat. */
  pastChatsSelectedThreadId: string | null;
  messagesByThread: Record<string, StoredInsightsChatMessage[]>;
  inputValueByThread: Record<string, string>;
}

interface InsightsChatPersistState {
  threads: InsightsChatThread[];
  activeThreadId: string;
  pastChatsSelectedThreadId: string | null;
  messages: InsightsChatMessage[];
  inputValue: string;
  isThreadLoading: boolean;
}

interface PersistedInsightsChatThread {
  threadId: string;
  startedAt: number;
  inputValue: string;
  updatedAt: number;
  previewText?: string;
  messageCount?: number;
  encrypted?: boolean;
  hasLegacyMessages?: boolean;
}

interface PersistedInsightsChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
  images?: string[];
  imageStorageIds?: string[];
  imageUrls?: Array<string | null>;
  encrypted?: boolean;
}

const createInitialMessage = (): StoredInsightsChatMessage => ({
  id: uuidv4(),
  role: "assistant",
  content: INITIAL_MESSAGE,
  timestamp: new Date(),
});

const createNewThread = (): InsightsChatThread => ({
  id: uuidv4(),
  startedAt: new Date(),
  previewText: undefined,
  messageCount: 1,
});

const createInitialStore = (): InsightsChatPersistStore => {
  const initialThread = createNewThread();
  return {
    threads: [initialThread],
    activeThreadId: initialThread.id,
    pastChatsSelectedThreadId: null,
    messagesByThread: {
      [initialThread.id]: [createInitialMessage()],
    },
    inputValueByThread: {
      [initialThread.id]: "",
    },
  };
};

const getThreadPreview = (messages: InsightsChatMessage[]) =>
  messages.find((message) => message.role === "user" && message.content.trim().length > 0)?.content;

const serializeThreadForSync = (thread: InsightsChatThread, inputValue: string) =>
  JSON.stringify({
    startedAt: thread.startedAt.getTime(),
    inputValue,
    previewText: thread.previewText || null,
    messageCount: thread.messageCount,
  });

const serializeMessagesForSync = (messages: InsightsChatMessage[]) =>
  JSON.stringify(
    messages
      .filter((message) => !message.isError)
      .map((message) => ({
        id: message.id,
        role: message.role,
        content: message.content,
        timestamp: message.timestamp.getTime(),
        images: message.images || null,
      }))
  );

const arraysEqual = (a?: string[], b?: string[]) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
};

const mergeMessageStorageMetadata = (
  previousMessages: StoredInsightsChatMessage[],
  nextMessages: InsightsChatMessage[]
): StoredInsightsChatMessage[] => {
  const previousById = new Map(previousMessages.map((message) => [message.id, message] as const));
  return nextMessages.map((message) => {
    const previous = previousById.get(message.id);
    if (previous && arraysEqual(previous.images, message.images)) {
      return {
        ...message,
        imageStorageIds: previous.imageStorageIds,
      };
    }
    return { ...message };
  });
};

const shouldPersistThread = (
  messages: InsightsChatMessage[],
  inputValue: string
) => {
  const hasUserMessage = messages.some(
    (message) =>
      message.role === "user" &&
      (message.content.trim().length > 0 || (message.images?.length ?? 0) > 0)
  );
  const hasDraftInput = inputValue.trim().length > 0;
  return hasUserMessage || hasDraftInput;
};

interface InsightsChatPersistContextType {
  state: InsightsChatPersistState;
  setMessages: (messages: InsightsChatMessage[] | ((prev: InsightsChatMessage[]) => InsightsChatMessage[])) => void;
  setInputValue: (value: string) => void;
  setActiveThread: (threadId: string) => void;
  deleteThread: (threadId: string) => Promise<void>;
  resetChat: () => void;
  hasUnsavedContent: () => boolean;
}

const InsightsChatPersistContext = createContext<InsightsChatPersistContextType | undefined>(undefined);

export const InsightsChatPersistProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const { status: encryptionStatus, encrypt: encryptFn, decrypt: decryptFn } = useEncryption();
  const shouldSyncWithAccount = !!user && !DEV_BYPASS;

  const [store, setStore] = useState<InsightsChatPersistStore>(() => createInitialStore());

  const remoteThreads = useQuery(
    api.insightsChats.getThreads,
    shouldSyncWithAccount ? {} : "skip"
  );
  const hasRemoteActiveThread =
    shouldSyncWithAccount &&
    Array.isArray(remoteThreads) &&
    remoteThreads.some((thread: any) => thread.threadId === store.activeThreadId);
  const remoteActiveMessages = useQuery(
    api.insightsChats.getThreadMessages,
    hasRemoteActiveThread ? { threadId: store.activeThreadId } : "skip"
  );
  const upsertThreadMutation = useMutation(api.insightsChats.upsertThread);
  const replaceThreadMessagesMutation = useMutation(api.insightsChats.replaceThreadMessages);
  const deleteThreadMutation = useMutation(api.insightsChats.deleteThread);
  const migrateLegacyThreadMessagesMutation = useMutation(api.insightsChats.migrateLegacyThreadMessages);
  const generateUploadUrlMutation = useMutation(api.entries.generateUploadUrl);

  const isHydratedRef = useRef(false);
  const hydratedUserIdRef = useRef<string | null>(null);
  const loadedThreadIdsRef = useRef<Set<string>>(new Set());
  const migratedLegacyThreadsRef = useRef<Set<string>>(new Set());
  const lastPersistedThreadsRef = useRef<Map<string, string>>(new Map());
  const lastPersistedMessagesRef = useRef<Map<string, string>>(new Map());
  const persistTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const storagePayloadCacheRef = useRef<Map<string, Promise<string>>>(new Map());

  const getStoredEncryptedPayload = async (storageId: string, url: string | null | undefined) => {
    if (!url) {
      throw new Error(`Missing download URL for storage file ${storageId}`);
    }

    const cached = storagePayloadCacheRef.current.get(storageId);
    if (cached) {
      return await cached;
    }

    const request = downloadEncryptedPayload(url).catch((error) => {
      storagePayloadCacheRef.current.delete(storageId);
      throw error;
    });
    storagePayloadCacheRef.current.set(storageId, request);
    return await request;
  };

  const loadEncryptedImagesFromStorage = async (
    storageIds?: string[],
    imageUrls?: Array<string | null>
  ) => {
    if (!storageIds || storageIds.length === 0) {
      return undefined;
    }

    return await Promise.all(
      storageIds.map(async (storageId, index) => {
        return await getStoredEncryptedPayload(storageId, imageUrls?.[index]);
      })
    );
  };

  const uploadEncryptedImagesToStorage = async (ciphertexts?: string[]) => {
    if (!ciphertexts || ciphertexts.length === 0) {
      return undefined;
    }

    return await Promise.all(
      ciphertexts.map(async (ciphertext) => {
        const uploadUrl = await generateUploadUrlMutation({});
        return await uploadEncryptedPayload(uploadUrl, ciphertext);
      })
    );
  };

  useEffect(() => {
    return () => {
      if (persistTimeoutRef.current) {
        clearTimeout(persistTimeoutRef.current);
        persistTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!shouldSyncWithAccount) {
      const initialStore = createInitialStore();
      setStore(initialStore);
      isHydratedRef.current = true;
      hydratedUserIdRef.current = null;
      loadedThreadIdsRef.current = new Set(Object.keys(initialStore.messagesByThread));
      migratedLegacyThreadsRef.current = new Set();
      lastPersistedThreadsRef.current = new Map();
      lastPersistedMessagesRef.current = new Map();
      return;
    }

    if (hydratedUserIdRef.current !== user.id) {
      const initialStore = createInitialStore();
      setStore(initialStore);
      isHydratedRef.current = false;
      hydratedUserIdRef.current = user.id;
      loadedThreadIdsRef.current = new Set(Object.keys(initialStore.messagesByThread));
      migratedLegacyThreadsRef.current = new Set();
      lastPersistedThreadsRef.current = new Map();
      lastPersistedMessagesRef.current = new Map();
    }
  }, [shouldSyncWithAccount, user?.id]);

  useEffect(() => {
    if (!shouldSyncWithAccount || remoteThreads === undefined || isHydratedRef.current || encryptionStatus !== "ready") {
      return;
    }

    const hydrateThreads = async () => {
      const decryptedThreads = await Promise.all(
        (remoteThreads as PersistedInsightsChatThread[]).map(async (thread) => {
          const inputValue =
            thread.encrypted && thread.inputValue
              ? await decryptFn(thread.inputValue)
              : thread.inputValue || "";
          const previewText =
            thread.encrypted && thread.previewText
              ? await decryptFn(thread.previewText)
              : thread.previewText;

          return {
            thread: {
              id: thread.threadId,
              startedAt: new Date(thread.startedAt),
              previewText,
              messageCount: thread.messageCount ?? 0,
            },
            inputValue,
            updatedAt: thread.updatedAt,
          };
        })
      );

      const sorted = decryptedThreads.sort((a, b) => b.updatedAt - a.updatedAt);

      if (sorted.length === 0) {
        const initialStore = createInitialStore();
        setStore(initialStore);
        loadedThreadIdsRef.current = new Set(Object.keys(initialStore.messagesByThread));
        lastPersistedThreadsRef.current = new Map();
        lastPersistedMessagesRef.current = new Map();
      } else {
        setStore((prev) => {
          const nextThreads = sorted.map(({ thread }) => thread);
          const nextActiveThreadId = nextThreads.some((thread) => thread.id === prev.activeThreadId)
            ? prev.activeThreadId
            : nextThreads[0].id;

          return {
            threads: nextThreads,
            activeThreadId: nextActiveThreadId,
            pastChatsSelectedThreadId: null,
            messagesByThread: nextActiveThreadId in prev.messagesByThread
              ? { [nextActiveThreadId]: prev.messagesByThread[nextActiveThreadId] }
              : {},
            inputValueByThread: Object.fromEntries(
              sorted.map(({ thread, inputValue }) => [thread.id, inputValue])
            ),
          };
        });

        lastPersistedThreadsRef.current = new Map(
          sorted.map(({ thread, inputValue }) => [
            thread.id,
            serializeThreadForSync(thread, inputValue),
          ])
        );
      }

      isHydratedRef.current = true;
    };

    hydrateThreads();
  }, [remoteThreads, shouldSyncWithAccount, encryptionStatus, decryptFn]);

  useEffect(() => {
    if (!shouldSyncWithAccount || remoteThreads === undefined || !isHydratedRef.current) {
      return;
    }

    const legacyThreads = (remoteThreads as PersistedInsightsChatThread[]).filter(
      (thread) => thread.hasLegacyMessages && !migratedLegacyThreadsRef.current.has(thread.threadId)
    );

    if (legacyThreads.length === 0) {
      return;
    }

    legacyThreads.forEach((thread) => {
      migratedLegacyThreadsRef.current.add(thread.threadId);
      void migrateLegacyThreadMessagesMutation({ threadId: thread.threadId }).catch((error) => {
        console.error("Failed to migrate legacy Insights chat thread:", error);
        migratedLegacyThreadsRef.current.delete(thread.threadId);
      });
    });
  }, [remoteThreads, shouldSyncWithAccount, migrateLegacyThreadMessagesMutation]);

  useEffect(() => {
    if (
      !hasRemoteActiveThread ||
      remoteActiveMessages === undefined ||
      encryptionStatus !== "ready" ||
      loadedThreadIdsRef.current.has(store.activeThreadId)
    ) {
      return;
    }

    const threadId = store.activeThreadId;

    const hydrateActiveThreadMessages = async () => {
      const decryptedMessages = await Promise.all(
        (remoteActiveMessages as PersistedInsightsChatMessage[]).map(async (message) => {
          const encryptedImages =
            message.imageStorageIds && message.imageStorageIds.length > 0
              ? await loadEncryptedImagesFromStorage(message.imageStorageIds, message.imageUrls)
              : message.images;

          const content =
            message.encrypted && message.content
              ? await decryptFn(message.content)
              : message.content;
          const images = encryptedImages
            ? await Promise.all(
                encryptedImages.map(async (image) =>
                  message.encrypted ? await decryptFn(image) : image
                )
              )
            : undefined;

          return {
            id: message.id,
            role: message.role,
            content,
            timestamp: new Date(message.timestamp),
            images,
            imageStorageIds: message.imageStorageIds,
          } satisfies StoredInsightsChatMessage;
        })
      );

      const TRANSIENT_CONTENT = [
        "I'm having trouble responding right now. Please try again!",
        "Listening...",
      ];
      let filteredMessages = decryptedMessages.filter(
        (msg) => !TRANSIENT_CONTENT.some((c) => msg.content === c || msg.content.startsWith("Listening"))
      );
      // Strip any trailing user message that has no subsequent assistant reply
      // (orphaned from a failed or interrupted exchange)
      while (filteredMessages.length > 0 && filteredMessages[filteredMessages.length - 1].role === "user") {
        filteredMessages = filteredMessages.slice(0, -1);
      }
      const nextMessages = filteredMessages.length > 0 ? filteredMessages : [createInitialMessage()];

      setStore((prev) => ({
        ...prev,
        messagesByThread: {
          ...prev.messagesByThread,
          [threadId]: nextMessages,
        },
      }));

      loadedThreadIdsRef.current.add(threadId);
      lastPersistedMessagesRef.current.set(threadId, serializeMessagesForSync(nextMessages));
    };

    hydrateActiveThreadMessages();
  }, [remoteActiveMessages, hasRemoteActiveThread, store.activeThreadId, encryptionStatus, decryptFn]);

  useEffect(() => {
    if (!shouldSyncWithAccount || !isHydratedRef.current || !user || encryptionStatus !== "ready") {
      return;
    }

    const changedThreadIds = store.threads
      .map((thread) => {
        const messages = store.messagesByThread[thread.id] ?? [];
        const inputValue = store.inputValueByThread[thread.id] ?? "";
        if (!shouldPersistThread(messages, inputValue)) {
          return null;
        }

        const serializedThread = serializeThreadForSync(thread, inputValue);
        const serializedMessages = serializeMessagesForSync(messages);
        const threadChanged = lastPersistedThreadsRef.current.get(thread.id) !== serializedThread;
        const messagesChanged = lastPersistedMessagesRef.current.get(thread.id) !== serializedMessages;

        return threadChanged || messagesChanged ? thread.id : null;
      })
      .filter((threadId): threadId is string => threadId !== null);

    if (changedThreadIds.length === 0) {
      return;
    }

    if (persistTimeoutRef.current) {
      clearTimeout(persistTimeoutRef.current);
    }

    persistTimeoutRef.current = setTimeout(async () => {
      for (const threadId of changedThreadIds) {
        const thread = store.threads.find((candidate) => candidate.id === threadId);
        if (!thread) continue;

        const messages = store.messagesByThread[threadId] ?? [];
        const inputValue = store.inputValueByThread[threadId] ?? "";
        const serializedThread = serializeThreadForSync(thread, inputValue);
        const serializedMessages = serializeMessagesForSync(messages);

        try {
          const encryptedInputValue = await encryptFn(inputValue);
          const encryptedPreviewText = thread.previewText
            ? await encryptFn(thread.previewText)
            : undefined;

          await upsertThreadMutation({
            threadId,
            startedAt: thread.startedAt.getTime(),
            inputValue: encryptedInputValue,
            previewText: encryptedPreviewText,
            messageCount: thread.messageCount,
            encrypted: true,
          });
          lastPersistedThreadsRef.current.set(threadId, serializedThread);

          if (lastPersistedMessagesRef.current.get(threadId) !== serializedMessages) {
            const encryptedMessages = await Promise.all(
              (store.messagesByThread[threadId] ?? []).filter((message) => !message.isError).map(async (message) => {
                let imageStorageIds = message.imageStorageIds;
                if (message.images && message.images.length > 0 && (!imageStorageIds || imageStorageIds.length === 0)) {
                  const encryptedImages = await Promise.all(
                    message.images.map(async (image) => await encryptFn(image))
                  );
                  imageStorageIds = await uploadEncryptedImagesToStorage(encryptedImages);
                }

                return {
                  id: message.id,
                  role: message.role,
                  content: await encryptFn(message.content),
                  timestamp: message.timestamp.getTime(),
                  imageStorageIds: imageStorageIds as any,
                };
              })
            );

            await replaceThreadMessagesMutation({
              threadId,
              messages: encryptedMessages,
              encrypted: true,
            });

            setStore((prev) => ({
              ...prev,
              messagesByThread: {
                ...prev.messagesByThread,
                [threadId]: (prev.messagesByThread[threadId] ?? []).map((message) => ({
                  ...message,
                  imageStorageIds:
                    encryptedMessages.find((encryptedMessage) => encryptedMessage.id === message.id)?.imageStorageIds ??
                    message.imageStorageIds,
                })),
              },
            }));

            lastPersistedMessagesRef.current.set(threadId, serializedMessages);
          }
        } catch (error) {
          console.error("Failed to persist Insights chat thread:", error);
        }
      }
    }, 2000);
  }, [store, shouldSyncWithAccount, upsertThreadMutation, replaceThreadMessagesMutation, user?.id, encryptionStatus, encryptFn]);

  const setMessages = useCallback((messagesOrUpdater: InsightsChatMessage[] | ((prev: InsightsChatMessage[]) => InsightsChatMessage[])) => {
    setStore((prev) => {
      let threads = prev.threads;
      let activeThreadId = prev.activeThreadId;

      if (threads.length === 0) {
        const fallbackThread = createNewThread();
        threads = [fallbackThread];
        activeThreadId = fallbackThread.id;
      }

      const activeThreadIndex = threads.findIndex((thread) => thread.id === activeThreadId);
      const resolvedIndex = activeThreadIndex >= 0 ? activeThreadIndex : 0;
      const currentThread = threads[resolvedIndex];
      const currentMessages = prev.messagesByThread[currentThread.id] ?? [];
      const updatedMessages = typeof messagesOrUpdater === "function"
        ? messagesOrUpdater(currentMessages)
        : messagesOrUpdater;
      const mergedMessages = mergeMessageStorageMetadata(currentMessages, updatedMessages);
      const previewText = getThreadPreview(mergedMessages);

      const nextThreads = [...threads];
      nextThreads[resolvedIndex] = {
        ...currentThread,
        previewText,
        messageCount: mergedMessages.length,
      };

      loadedThreadIdsRef.current.add(currentThread.id);

      return {
        ...prev,
        threads: nextThreads,
        activeThreadId: nextThreads[resolvedIndex].id,
        messagesByThread: {
          ...prev.messagesByThread,
          [currentThread.id]: mergedMessages,
        },
      };
    });
  }, []);

  const setInputValue = useCallback((value: string) => {
    setStore((prev) => {
      let threads = prev.threads;
      let activeThreadId = prev.activeThreadId;

      if (threads.length === 0) {
        const fallbackThread = createNewThread();
        threads = [fallbackThread];
        activeThreadId = fallbackThread.id;
      }

      const activeThreadIndex = threads.findIndex((thread) => thread.id === activeThreadId);
      const resolvedIndex = activeThreadIndex >= 0 ? activeThreadIndex : 0;
      const currentThread = threads[resolvedIndex];

      return {
        ...prev,
        activeThreadId: currentThread.id,
        inputValueByThread: {
          ...prev.inputValueByThread,
          [currentThread.id]: value,
        },
      };
    });
  }, []);

  const setActiveThread = useCallback((threadId: string) => {
    setStore((prev) => {
      const exists = prev.threads.some((thread) => thread.id === threadId);
      if (!exists) {
        return prev;
      }
      return {
        ...prev,
        activeThreadId: threadId,
        pastChatsSelectedThreadId: threadId,
      };
    });
  }, []);

  const deleteThread = useCallback(async (threadId: string) => {
    const existingThread = store.threads.find((thread) => thread.id === threadId);
    if (!existingThread) {
      return;
    }

    setStore((prev) => {
      const remainingThreads = prev.threads.filter((thread) => thread.id !== threadId);
      if (remainingThreads.length === 0) {
        return createInitialStore();
      }

      const nextActiveThreadId = prev.activeThreadId === threadId
        ? remainingThreads[0].id
        : prev.activeThreadId;

      const { [threadId]: _removedMessages, ...remainingMessages } = prev.messagesByThread;
      const { [threadId]: _removedInput, ...remainingInputs } = prev.inputValueByThread;

      return {
        threads: remainingThreads,
        activeThreadId: nextActiveThreadId,
        pastChatsSelectedThreadId:
          prev.pastChatsSelectedThreadId === threadId ? null : prev.pastChatsSelectedThreadId,
        messagesByThread: remainingMessages,
        inputValueByThread: remainingInputs,
      };
    });

    loadedThreadIdsRef.current.delete(threadId);
    lastPersistedThreadsRef.current.delete(threadId);
    lastPersistedMessagesRef.current.delete(threadId);

    if (!shouldSyncWithAccount) {
      return;
    }

    try {
      await deleteThreadMutation({ threadId });
    } catch (error) {
      console.error("Failed to delete Insights chat thread:", error);
    }
  }, [deleteThreadMutation, shouldSyncWithAccount, store.threads]);

  const resetChat = useCallback(() => {
    const nextThread = createNewThread();
    const nextMessage = createInitialMessage();

    setStore((prev) => ({
      threads: [nextThread, ...prev.threads],
      activeThreadId: nextThread.id,
      pastChatsSelectedThreadId: nextThread.id,
      messagesByThread: {
        ...prev.messagesByThread,
        [nextThread.id]: [nextMessage],
      },
      inputValueByThread: {
        ...prev.inputValueByThread,
        [nextThread.id]: "",
      },
    }));

    loadedThreadIdsRef.current.add(nextThread.id);
  }, []);

  const activeThread =
    store.threads.find((thread) => thread.id === store.activeThreadId) ??
    store.threads[0] ??
    createNewThread();
  const activeMessages = store.messagesByThread[activeThread.id] ?? [];
  const activeInputValue = store.inputValueByThread[activeThread.id] ?? "";
  const isThreadLoading = hasRemoteActiveThread && !loadedThreadIdsRef.current.has(activeThread.id);

  const state = useMemo<InsightsChatPersistState>(() => ({
    threads: store.threads.length > 0 ? store.threads : [activeThread],
    activeThreadId: activeThread.id,
    pastChatsSelectedThreadId: store.pastChatsSelectedThreadId,
    messages: activeMessages,
    inputValue: activeInputValue,
    isThreadLoading,
  }), [store.threads, store.pastChatsSelectedThreadId, activeThread, activeMessages, activeInputValue, isThreadLoading]);

  const hasUnsavedContent = useCallback(() => {
    return activeMessages.length > 1 || activeInputValue.trim().length > 0;
  }, [activeMessages.length, activeInputValue]);

  return (
    <InsightsChatPersistContext.Provider value={{
      state,
      setMessages,
      setInputValue,
      setActiveThread,
      deleteThread,
      resetChat,
      hasUnsavedContent,
    }}>
      {children}
    </InsightsChatPersistContext.Provider>
  );
};

export const useInsightsChatPersist = () => {
  const context = useContext(InsightsChatPersistContext);
  if (context === undefined) {
    throw new Error("useInsightsChatPersist must be used within an InsightsChatPersistProvider");
  }
  return context;
};
